import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { API } from "@/App";
import { CheckCircle2, XCircle, ExternalLink, ArrowLeft, ArrowRight, Star, Clock, DollarSign, Award, Zap } from "lucide-react";
import { ARTICLES } from "@/data/propFirmArticles";

/**
 * SEO article page for a single prop firm.
 * Route: /prop-firms/:slug
 * Content lives in /app/frontend/src/data/propFirmArticles.js
 */
export default function PropFirmArticle() {
  const { slug } = useParams();
  const nav = useNavigate();
  const article = ARTICLES[slug];
  const [aff, setAff] = useState({});

  usePageMeta({
    title: article ? `${article.name} — Avis complet 2026 · ANATRADEX` : "Prop Firm · ANATRADEX",
    description: article?.metaDescription || "",
    og: article ? { title: `${article.name} — Avis ANATRADEX`, description: article.metaDescription } : {},
  });

  useEffect(() => {
    if (!article) { nav("/prop-firms", { replace: true }); return; }
    fetch(`${API}/public/affiliates`)
      .then(r => r.json()).then(d => setAff(d.ids || {})).catch(() => {});
  }, [article, nav]);

  // JSON-LD FAQ schema
  useEffect(() => {
    if (!article) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": (article.faq || []).map(q => ({
        "@type": "Question",
        "name": q.q,
        "acceptedAnswer": { "@type": "Answer", "text": q.a }
      })),
    });
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch {} };
  }, [article]);

  if (!article) return null;

  const refId = aff[slug] || "ANATRADEX";
  const affiliateUrl = article.baseUrl.replace("REF_PLACEHOLDER", refId);

  return (
    <div className="min-h-screen bg-bg text-white/85" data-testid="propfirm-article-page">
      {/* NAV */}
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-accent-blue to-blue-600 flex items-center justify-center font-bold text-lg">P</div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Prop Firm Review</div>
            </div>
          </Link>
          <Link to="/prop-firms" className="text-sm text-white/60 hover:text-white flex items-center gap-1">
            <ArrowLeft size={13} /> Comparateur
          </Link>
        </div>
      </nav>

      {/* Header */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-accent-blue/80 mb-2">Avis complet · Février 2026</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4 flex items-center gap-3" data-testid="article-title">
          <span className="text-4xl">{article.flag}</span> {article.name}
        </h1>
        <div className="flex items-center gap-3 text-sm text-white/60 mb-6">
          <span className="inline-flex items-center gap-1"><Star size={13} className="text-yellow-400 fill-yellow-400" /> {article.rating}/5</span>
          <span>·</span>
          <span>${article.price100k} pour 100k</span>
          <span>·</span>
          <span>Split {article.split}</span>
        </div>

        {/* Verdict box */}
        <div className="panel p-5 mb-8 border-l-2 border-accent-blue">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Verdict ANATRADEX</div>
          <p className="text-white/85 leading-relaxed">{article.verdict}</p>
        </div>

        {/* CTA sticky */}
        <div className="panel p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-10 bg-gradient-to-r from-accent-blue/10 to-transparent border-accent-blue/30">
          <div className="text-sm">
            <div className="font-heading font-semibold">Prêt à tenter le challenge {article.name} ?</div>
            <div className="text-white/50 text-xs">Cliquez pour accéder à l'offre officielle</div>
          </div>
          <a href={affiliateUrl} target="_blank" rel="noopener noreferrer nofollow sponsored"
             className="btn btn-primary shrink-0" data-testid="article-cta-top">
            Voir {article.name} <ExternalLink size={12} />
          </a>
        </div>

        {/* Sections */}
        <Section title="Présentation">
          <p>{article.intro}</p>
        </Section>

        <Section title="Règles du challenge">
          <div className="grid sm:grid-cols-2 gap-3">
            {article.rules.map((r) => (
              <div key={r.label} className="bg-white/5 rounded-md p-3 border border-white/10">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{r.label}</div>
                <div className="font-mono text-sm">{r.value}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Les points forts">
          <ul className="space-y-2">
            {article.strengths.map(s => (
              <li key={s} className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-accent-green mt-0.5 shrink-0" /> <span>{s}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Les points faibles">
          <ul className="space-y-2">
            {article.weaknesses.map(w => (
              <li key={w} className="flex items-start gap-2">
                <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" /> <span>{w}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Comparaison avec les autres prop firms">
          <p>{article.comparison}</p>
        </Section>

        <Section title={`Pour qui ${article.name} est-elle idéale ?`}>
          <p>{article.audience}</p>
        </Section>

        {/* FAQ */}
        <Section title="Questions fréquentes">
          <div className="space-y-4">
            {(article.faq || []).map((f, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/10 rounded-md p-4">
                <div className="font-heading font-semibold mb-2 text-white">{f.q}</div>
                <div className="text-white/70 text-sm leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Bottom CTA */}
        <div className="panel p-6 mt-10 text-center">
          <div className="font-heading text-xl font-bold mb-2">Passez à l'action</div>
          <p className="text-white/60 text-sm mb-4">
            Si {article.name} correspond à votre profil, ne perdez plus de temps.
          </p>
          <a href={affiliateUrl} target="_blank" rel="noopener noreferrer nofollow sponsored"
             className="btn btn-primary inline-flex" data-testid="article-cta-bottom">
            Voir l'offre {article.name} <ExternalLink size={13} />
          </a>
          <div className="text-[10px] text-white/30 mt-3">Lien affilié — cela ne modifie pas le prix pour vous.</div>
        </div>

        {/* Nav to other articles */}
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-white/10">
          <Link to="/prop-firms" className="btn btn-secondary" data-testid="article-back">
            <ArrowLeft size={13} /> Retour au comparateur
          </Link>
          <Link to="/quiz" className="btn btn-secondary">
            Refaire le quiz <ArrowRight size={13} />
          </Link>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-heading text-2xl font-bold mb-4">{title}</h2>
      <div className="text-white/70 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}
